import { MapIconsPins } from '../../constants/map-pin-icons';
import { MAP_ADDRESS_TYPES, MapAddressTypes } from '../../constants/map-address-types';
import { IAddress } from '@cemex-core/cmx-api/models/address/address.interface';
import { COUNTRY_CODE, GeoPlace, IRequestedAddressResponse } from '@cemex-core/cmx-api';
import { convertValueToMeters } from '@ordersnproducts/modules/order-builder/modules/location/helpers/location.helper';
import { removeDefaultAddressValue } from '../../utils/remove-default-address-value';

export class CmxMapConfig {
  private _address: string;
  private _mapIconPin = MapIconsPins.MAP_ICON_PIN;
  private _geoPlace: GeoPlace;
  private _zoom = 14;
  private _scrollWheel = false;
  private _addressType: MapAddressTypes = MAP_ADDRESS_TYPES.JOBSITE;
  private _radiusGeoPlace: GeoPlace;
  private _radius = 0;

  /**
   * Whether a map allows use of the scroll-wheel for zoomin in or out
   */
  get scrollWheel(): boolean {
    return this._scrollWheel;
  }
  set scrollWheel(value: boolean) {
    this._scrollWheel = value;
  }

  /**
   * The zoom-level of this map
   */
  get zoom(): number {
    return this._zoom;
  }
  set zoom(value: number) {
    this._zoom = value;
  }

  /**
   * A GeoPlace object which describes a location on the planet's surface.
   */
  get geoPlace(): GeoPlace {
    return this._geoPlace;
  }
  set geoPlace(value: GeoPlace) {
    this._geoPlace = value;
  }

  /**
   * The icon used by this map to show locations
   */
  get mapIconPin(): MapIconsPins {
    return this._mapIconPin;
  }
  set mapIconPin(value: MapIconsPins) {
    this._mapIconPin = value;
  }

  /**
   * The written address for the location shown on this map
   */
  get address(): string {
    return this._address;
  }
  set address(value: string) {
    this._address = value;
  }

  /**
   * The type of address shown on this map
   */
  get addressType(): MapAddressTypes {
    return this._addressType;
  }
  set addressType(value: MapAddressTypes) {
    this._addressType = value;
  }

  /**
   * A GeoPlace object which describes a location on the planet's surface.
   */
  get radiusGeoPlace(): GeoPlace {
    return this._radiusGeoPlace;
  }
  set radiusGeoPlace(value: GeoPlace) {
    this._radiusGeoPlace = value;
  }

  /**
   * The radius of the circle on this map
   */
  get radius(): number {
    return this._radius;
  }
  set radius(value: number) {
    this._radius = value;
  }

  /**
   * config can be also null because we need also initial state
   * @param {IAddressResponse} config
   * @param addressType
   */
  constructor(
    config: IAddress | IRequestedAddressResponse = null,
    addressType: MapAddressTypes = MAP_ADDRESS_TYPES.JOBSITE,
    configRadius: IAddress | IRequestedAddressResponse = null,
    distance: any = null,
  ) {
    this._address = config ? this.formatAddress(config) : '';
    this._geoPlace = config ? new GeoPlace(config.geoPlace) : null;
    this._addressType = addressType;
    this._radiusGeoPlace = configRadius ? new GeoPlace(configRadius.geoPlace) : null;
    this._radius = distance ? convertValueToMeters(distance.amount, distance.unit.unitCode) : 0;
  }

  /**
   * Provides a postal address template.
   * @param config
   */
  private formatAddress(config: IAddress | IRequestedAddressResponse): string {
    const countryCode = config.countryCode ? config.countryCode.trim() : '';
    const countriesDomicileNumFirst = [COUNTRY_CODE.USA];

    const streetName = config.streetName ? config.streetName : '';
    const domicileNum = config.domicileNum ? config.domicileNum : '';
    let fullStreetName = streetName + ' ' + domicileNum;

    if (countriesDomicileNumFirst.includes(countryCode as COUNTRY_CODE)) {
      fullStreetName = removeDefaultAddressValue(domicileNum) + ' ' + removeDefaultAddressValue(streetName);
    }

    const address = [
      fullStreetName.trim(),
      config.settlementDesc,
      config.regionDesc,
      removeDefaultAddressValue(config.cityDesc),
      removeDefaultAddressValue(config.postalCode),
      config.countryDesc,
    ];
    const fullAddress = address.filter(x => !!x);
    return fullAddress.join(', ');
  }
}
